import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { useI18n } from "@/contexts/I18nContext";   // Batch 19 · A2 (F-142): the button texts EN + AR
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { KeyRound, Mail, ShieldCheck } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { showAuthError } from "@/pages/Login";   // Batch 22 · PR B · item 43: the same lock pop-up on the 2FA screen

export default function VerifyLogin() {
  const nav = useNavigate();
  const location = useLocation();
  const { setSession } = useAuth();
  const state = location.state || {};
  const [code, setCode] = useState("");
  const [challengeId, setChallengeId] = useState(state.challenge_id || "");
  const [emailHint] = useState(state.email_hint || "");
  const [devCode, setDevCode] = useState(state.dev_code || "");
  const [deliveryFailed, setDeliveryFailed] = useState(!!state.delivery_failed);
  const [deliveryError, setDeliveryError] = useState(state.delivery_error || "");
  const [deliveryDetail, setDeliveryDetail] = useState(state.delivery_detail || "");
  const [busy, setBusy] = useState(false);
  const [useBackup, setUseBackup] = useState(false);
  const { t } = useI18n();
  const [cooldown, setCooldown] = useState(0);
  const [emailStale, setEmailStale] = useState(false); // ← after 20s, nudge to backup code
  const inputRef = useRef(null);

  useEffect(() => {
    if (!challengeId && !useBackup) nav("/login", { replace: true });
  }, [challengeId, useBackup, nav]);

  useEffect(() => { inputRef.current?.focus(); }, [useBackup]);
  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);
  // After 20 s on the OTP screen without the user submitting, most
  // "never-received-the-email" cases are Yahoo/Outlook silent spam
  // filtering. Nudge the user toward the backup-code rescue path.
  useEffect(() => {
    if (useBackup) return;
    const t = setTimeout(() => setEmailStale(true), 20000);
    return () => clearTimeout(t);
  }, [challengeId, useBackup]);

  const submit = async (e) => {
    e?.preventDefault();
    if (!code) return;
    setBusy(true);
    try {
      // Landing page depends on role — Platform Admins go to /platform
      // (Tenants tab is default there), everyone else goes to /.
      const landingFor = (u) =>
        u?.role === "Platform Admin" ? "/platform" : "/";
      if (useBackup) {
        const r = await api.post("/auth/2fa/recovery", {
          email: state.email, password: state.password, backup_code: code,
        });
        await setSession(r.data.token, r.data.user);
        toast.success("Signed in with a backup code");
        nav(landingFor(r.data.user), { replace: true });
      } else {
        const r = await api.post("/auth/2fa/verify",
          { challenge_id: challengeId, code });
        await setSession(r.data.token, r.data.user);
        if (r.data.backup_codes_missing) {
          toast.info("Save your backup codes to protect this account.");
          nav("/backup-codes-setup", { replace: true });
        } else {
          toast.success("Signed in");
          nav(landingFor(r.data.user), { replace: true });
        }
      }
    } catch (err) {
      await showAuthError(err, t, "Verification failed");
    } finally { setBusy(false); }
  };

  const resend = async () => {
    if (cooldown > 0) return;
    try {
      const r = await api.post("/auth/2fa/resend", { challenge_id: challengeId }, { _silent: true });
      setChallengeId(r.data.challenge_id);
      if (r.data.dev_code) setDevCode(r.data.dev_code); else setDevCode("");
      setDeliveryFailed(!!r.data.delivery_failed);
      setDeliveryError(r.data.delivery_error || "");
      setDeliveryDetail(r.data.delivery_detail || "");
      setCooldown(60);
      if (r.data.delivery_failed) {
        toast.error(`Email couldn't be sent (${r.data.delivery_error || "provider error"}).`);
      } else {
        toast.success("New code sent");
      }
    } catch (err) {
      await showAuthError(err, t, t("auth.sendNewCodeFailed"));
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-50 p-6 dark:bg-slate-950">
      <div className="w-full max-w-sm rounded-xl border bg-white p-6 shadow-sm dark:bg-slate-900 dark:border-slate-800"
           data-testid="verify-login">
        <div className="mb-4 flex items-center gap-2 text-emerald-600">
          <ShieldCheck className="h-5 w-5" />
          <span className="text-xs font-semibold uppercase tracking-wider">AZEX · Two-factor sign-in</span>
        </div>
        <h1 className="text-xl font-bold text-slate-900 dark:text-slate-100">
          {useBackup ? "Enter a backup code" : "Enter your 6-digit code"}
        </h1>
        <p className="mt-1 mb-4 text-xs text-muted-foreground">
          {useBackup
            ? "Use one of the single-use backup codes you saved when you set up 2FA."
            : (<>We just emailed a 6-digit code to <b>{emailHint}</b>. It expires in 5 minutes.</>)}
        </p>

        {devCode && (
          <div className="mb-3 rounded border border-amber-400 bg-amber-50 p-2 text-xs text-amber-900">
            <b>Dev fallback code:</b> <code>{devCode}</code> (email service not activated yet)
          </div>
        )}
        {deliveryFailed && !devCode && (
          <div className="mb-3 rounded border border-red-300 bg-red-50 p-2 text-xs text-red-800" data-testid="verify-delivery-failed">
            {(() => {
              const err = (deliveryError || "").toLowerCase();
              if (err.includes("429") || (deliveryDetail || "").toLowerCase().includes("quota"))
                return "Our email provider has temporarily paused new sends (monthly quota reached). Please contact your firm administrator — they can raise the quota or resend from another channel.";
              if (err.includes("422"))
                return "The email address on file was rejected by our provider. Please ask your firm administrator to verify your account email.";
              if (err.includes("email_not_configured") || err.includes("from_not_configured"))
                return "Email delivery is not configured on this environment. Please contact your administrator.";
              return t("auth.codeNotDelivered");
            })()}
            {deliveryError && (
              <div className="mt-1 font-mono text-[10px] opacity-80">
                Provider error: <b>{deliveryError}</b>
                {deliveryDetail && <> · {deliveryDetail.slice(0, 200)}</>}
              </div>
            )}
          </div>
        )}
        {!deliveryFailed && !devCode && !useBackup && (
          <div className="mb-3 rounded border border-emerald-200 bg-emerald-50 p-2 text-[11px] text-emerald-800"
               data-testid="verify-check-spam">
            Not seeing the email in a minute? Please also check your <b>Spam</b> / <b>Junk</b> folder — some providers (Yahoo, Outlook) occasionally route sign-in codes there.
          </div>
        )}
        {emailStale && !useBackup && !devCode && (
          <div className="mb-3 rounded border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900"
               data-testid="verify-backup-nudge">
            <div className="font-semibold mb-1">Still no code?</div>
            <div className="mb-2">
              Yahoo and Outlook sometimes silently filter sign-in codes. If you
              saved your <b>backup codes</b> when you set up 2FA, you can sign
              in with one right now.
            </div>
            <Button
              type="button"
              size="sm"
              variant="outline"
              className="border-amber-400 bg-white text-amber-900 hover:bg-amber-100"
              onClick={() => { setUseBackup(true); setCode(""); }}
              data-testid="verify-backup-nudge-btn"
            >
              Use a backup code instead
            </Button>
          </div>
        )}

        <form onSubmit={submit} className="space-y-3">
          <Input
            ref={inputRef}
            value={code}
            onChange={(e) => setCode(e.target.value)}
            placeholder={useBackup ? "XXXX-XXXX" : "123456"}
            inputMode={useBackup ? "text" : "numeric"}
            maxLength={useBackup ? 9 : 6}
            autoComplete="one-time-code"
            className="text-center text-lg tracking-widest"
            data-testid="verify-code-input"
          />
          <Button type="submit" disabled={busy || code.length < 4}
                    className="w-full bg-emerald-600 hover:bg-emerald-700"
                    data-testid="verify-submit">
            <KeyRound className="me-1 h-3 w-3" />
            {busy ? "Verifying…" : "Verify and sign in"}
          </Button>
        </form>

        <div className="mt-4 flex items-center justify-between text-xs">
          {!useBackup ? (
            <button className="text-emerald-600 hover:underline"
                      onClick={resend} disabled={cooldown > 0}
                      data-testid="verify-resend">
              <Mail className="me-1 inline h-3 w-3" />
              {cooldown > 0 ? t("auth.sendNewCodeIn").replace("{s}", String(cooldown)) : t("auth.sendNewCode")}
            </button>
          ) : <span />}
          <button className="text-emerald-600 hover:underline"
                   onClick={() => { setUseBackup(!useBackup); setCode(""); }}
                   data-testid="verify-toggle-backup">
            {useBackup ? "Use email code" : "Use a backup code"}
          </button>
        </div>

        <div className="mt-6 border-t pt-3 text-[10px] text-muted-foreground dark:border-slate-800">
          Lost access to your email <b>and</b> your backup codes? Ask your firm
          Partner or a Platform Administrator to issue an administrator-reset code.
        </div>
      </div>
    </div>
  );
}
